import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  query,
  where,
  onSnapshot,
  serverTimestamp,
  orderBy,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';
import { parseMoneyLoose, parsePercentLoose, roundMoneyToCents } from '../utils/stagingAmountFormat';

const COLLECTION = 'lenderStagedProperties';

/** @see normalizeLoanTranchesForStaged */
export const STAGED_TRANCHE_DATES_INVALID = 'STAGED_TRANCHE_DATES_INVALID';

/**
 * True when a maturity date is set but missing start or not strictly after start (YYYY-MM-DD).
 */
export function isStagedTrancheDateOrderInvalid(startDate, maturityDate) {
  const s = String(startDate ?? '').trim().slice(0, 10);
  const e = String(maturityDate ?? '').trim().slice(0, 10);
  if (!e) return false;
  if (!s || e <= s) return true;
  return false;
}

/** @returns {number} index of first invalid row, or -1 */
export function findFirstInvalidStagedTrancheDateRow(rows) {
  if (!Array.isArray(rows)) return -1;
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i] || {};
    if (isStagedTrancheDateOrderInvalid(r.startDate, r.maturityDate)) return i;
  }
  return -1;
}

/**
 * @param {unknown} rows
 * @returns {Array<{ id: string, amount: number, startDate: string, interestRate: number | null, maturityDate: string | null, note: string | null }>}
 */
export function normalizeLoanTranchesForStaged(rows) {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  const todayIso = new Date().toISOString().slice(0, 10);
  const out = [];
  for (let i = 0; i < rows.length; i += 1) {
    const r = rows[i] || {};
    const amountParsed = parseMoneyLoose(r.amount);
    const raw = Number.isFinite(amountParsed) ? amountParsed : Number(r.amount);
    const amount = roundMoneyToCents(raw);
    const startDate = r.startDate != null ? String(r.startDate).trim().slice(0, 10) : '';
    if (!Number.isFinite(amount) || amount <= 0 || !startDate) continue;
    const rateRaw = r.interestRate;
    const rateParsed = rateRaw != null && rateRaw !== '' ? parsePercentLoose(rateRaw) : NaN;
    let interestRate = null;
    if (rateRaw != null && rateRaw !== '') {
      if (Number.isFinite(rateParsed)) interestRate = rateParsed;
      else if (!Number.isNaN(Number(rateRaw))) interestRate = Number(rateRaw);
    }
    const mat = r.maturityDate != null ? String(r.maturityDate).trim().slice(0, 10) : '';
    if (mat && mat < todayIso) continue;
    if (mat && startDate && mat <= startDate) {
      throw new Error(STAGED_TRANCHE_DATES_INVALID);
    }
    const note = r.note != null && String(r.note).trim() ? String(r.note).trim().slice(0, 500) : null;
    const id = r.id && String(r.id).trim() ? String(r.id).trim() : `tr_${Date.now()}_${i}`;
    out.push({
      id,
      amount,
      startDate,
      interestRate,
      maturityDate: mat || null,
      note,
    });
  }
  return out;
}

/**
 * @param {{ lenderUid: string, clientId: string, address: string, city?: string, province?: string, postalCode?: string, notes?: string, loanTranches?: unknown }} data
 */
export async function createLenderStagedProperty(data) {
  const { lenderUid, clientId, address, city, province, postalCode, notes, loanTranches } = data;
  if (!lenderUid || !clientId || !String(address || '').trim()) {
    throw new Error('lenderUid, clientId and address required');
  }
  const tranches = normalizeLoanTranchesForStaged(loanTranches);
  const ref = await addDoc(collection(firestore, COLLECTION), {
    lenderUid,
    clientId,
    address: String(address).trim(),
    city: city != null && String(city).trim() ? String(city).trim() : '',
    province: province != null && String(province).trim() ? String(province).trim() : '',
    postalCode: postalCode != null && String(postalCode).trim() ? String(postalCode).trim() : '',
    notes: notes != null && String(notes).trim() ? String(notes).trim().slice(0, 500) : '',
    loanTranches: tranches,
    createdAt: serverTimestamp(),
  });
  return ref.id;
}

/**
 * @param {string} stagedId
 * @param {{ loanTranches: unknown }} data
 */
export async function updateLenderStagedPropertyLoanTranches(stagedId, data) {
  if (!stagedId) return;
  const tranches = normalizeLoanTranchesForStaged(data?.loanTranches);
  await updateDoc(doc(firestore, COLLECTION, stagedId), {
    loanTranches: tranches,
  });
}

export async function deleteLenderStagedProperty(stagedId) {
  if (!stagedId) return;
  await deleteDoc(doc(firestore, COLLECTION, stagedId));
}

/**
 * @param {string} lenderUid
 * @param {(rows: Array<{ id: string, [key: string]: any }>) => void} callback
 * @returns {() => void}
 */
export function subscribeLenderStagedProperties(lenderUid, callback) {
  if (!lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('lenderUid', '==', lenderUid),
    orderBy('createdAt', 'asc')
  );
  return onSnapshot(
    q,
    (snap) => callback(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    (err) => {
      console.warn('[lenderStagedProperties]', err?.code, err?.message);
      callback([]);
    }
  );
}
